/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import get from "lodash/get";
import { LazyLoadImages, ResponsiveImage } from "@quintype/components";
import { CustomLink } from "../custom-link";
import format from "date-fns/format";
import PropTypes from "prop-types";

export function AuthorName({ author, index, authorsLength, isLink }) {
  const spaceHolder = `\xa0 &`;
  return (
    <h3 className={`${isLink ? "author__name--is-link" : ""} author__name`}>
      {index !== 0 && (index === authorsLength - 1 ? spaceHolder : ",")}{" "}
      <h3 className="multiple-author-style">{author.name}</h3>
    </h3>
  );
}
AuthorName.propTypes = {
  author: PropTypes.object,
  index: PropTypes.number,
  authorsLength: PropTypes.number,
  isLink: PropTypes.bool
};
const getTimeZone = config => {
  const timeZoneStampMap = {
    IST: "Asia/Calcutta"
  };
  const timeStampString = get(config, ["themeAttributes", "story-timeformat"], "IST");
  return timeZoneStampMap[timeStampString];
};

export function getTimeStampOptions(config) {
  return {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: getTimeZone(config),
    hour: "numeric",
    minute: "numeric"
  };
}

export function AuthorsNameList({ authors = [], authorsLength, isLink }) {
  const getLinkedAuthorName = (author = {}, index) => (
    <CustomLink href={`/author/${author.slug}`} key={author.id}>
      <AuthorName author={author} index={index} authorsLength={authorsLength} isLink />
    </CustomLink>
  );

  return authors.map((author, index, authors) =>
    isLink ? (
      getLinkedAuthorName(author, index)
    ) : (
      <AuthorName author={author} index={index} authorsLength={authorsLength} key={author.id} />
    )
  );
}

export function AuthorImage({ "avatar-url": image, "avatar-s3-key": imageS3, metadata = {}, name }) {
  if (imageS3) {
    return (
      <div className="author__avatar">
        <LazyLoadImages margin={"0px"}>
          <ResponsiveImage
            slug={imageS3}
            metadata={metadata}
            aspectRatio={[1, 1]}
            defaultWidth={48}
            imgParams={{ auto: ["format", "compress"] }}
            alt={name}
          />
        </LazyLoadImages>
      </div>
    );
  }
  if (image) {
    return (
      <div className="author__avatar">
        <img src={image} loading="lazy" />
      </div>
    );
  }
  return null;
}
AuthorImage.propTypes = {
  "avatar-url": PropTypes.string,
  "avatar-s3-key": PropTypes.string,
  metadata: PropTypes.object,
  name: PropTypes.string
};
export function AuthorsImageList({ authors = [], isLink }) {
  const getLinkedAuthorImage = (author = {}) => (
    <CustomLink href={`/author/${author.slug}`} key={author.id} className="author__avatar-wrapper--is-link">
      <AuthorImage {...author} />
    </CustomLink>
  );

  return authors.map(author => (isLink ? getLinkedAuthorImage(author) : <AuthorImage {...author} />));
}

export function FallbackAuthorImage() {
  // TODO: replace with a fallback image. and use it in AuthorImage component.
  return null;
}

export function dateFormatter(value, unit, suffix, date, defaultFormatter) {
  return format(new Date(date), "DD MMM, YYYY [at] HH:mm");
}

export function getAuthorNameKeyword() {
  return <span className="author-name-keyword">By </span>;
}

export function getStoryPubKeyword() {
  return <span className="pub-keyword">Published on : </span>;
}

export function getDevider() {
  return <span className="pub-keyword"> | </span>;
}
